import { useTheme } from "@mui/material";
import { useModalStyles } from "./useModalStyles";

export const useListPageStyles = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens } = useModalStyles();

  const borderColor = isDark ? "rgba(148,163,184,0.2)" : "#e5e9ed";
  const panelBg = isDark ? "#141720" : "#ffffff";
  const hoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(13,148,136,0.08)";

  const cardSx = {
    borderRadius: "14px",
    border: `1px solid ${borderColor}`,
    backgroundColor: panelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 1px 2px rgba(15,23,42,0.04), 0 2px 8px rgba(15,23,42,0.06)",
  };

  const filterPanelSx = {
    ...cardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
    },
    "& .MuiTextField-root": {
      height: "42px",
      minHeight: "42px !important",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${tokens.inputBg} !important`,
      color: tokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${tokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${tokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": {
      color: tokens.textPrimary,
      fontSize: "0.88rem",
    },
    "& input::selection": {
      color: tokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: `${isDark ? "#376c9f" : "#f1f5f9"} !important`,
      "& fieldset": {
        borderColor: `${isDark ? "#7fb1df" : tokens.inputBorder} !important`,
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: isDark ? "#ffffff" : tokens.textPrimary,
      caretColor: isDark ? "#ffffff" : tokens.textPrimary,
      WebkitBoxShadow: `0 0 0 100px ${isDark ? "#376c9f" : "#f1f5f9"} inset`,
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${tokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
      color: tokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": {
      color: tokens.textSecondary,
    },
    "& .rmdp-container": {
      width: "100%",
      height: "42px",
      overflow: "visible",
      zIndex: 30,
    },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": {
      height: "auto !important",
      minHeight: "unset !important",
      overflow: "visible",
    },
    "& .rmdp-wrapper": {
      zIndex: "1600 !important",
      position: "absolute",
    },
  };

  const filterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: tokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": {
      color: "#fff",
      fontWeight: 700,
    },
    "& .MuiButton-startIcon": {
      color: "#fff",
    },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const exportMenuPaperSx = {
    width: "260px",
    maxWidth: "90vw",
    mt: 0.75,
    p: 0.75,
    borderRadius: "12px",
    border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
    backgroundColor: isDark ? "#141720" : "#ffffff",
    backgroundImage: "none",
    boxShadow: isDark ? "0 18px 42px rgba(0,0,0,0.45)" : "0 14px 32px rgba(15,23,42,0.14)",
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      mr: 1,
      "& svg": { fontSize: "18px" },
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#2f9b8f",
      backgroundColor: "#2f9b8f",
      boxShadow: "0 10px 24px rgba(20,184,166,0.28)",
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)",
      borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)",
      backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
    },
  };

  const exportBtnSx = {
    display: "flex", alignItems: "center", gap: 0.8,
    px: 1.6, height: 36,
    border: `1px solid ${tokens.accent}`,
    borderRadius: "10px",
    backgroundColor: "transparent",
    color: tokens.accent,
    cursor: "pointer",
    fontSize: "0.82rem", fontWeight: 700,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: `${tokens.accent}14`,
      "& .MuiSvgIcon-root": { color: tokens.accent },
    },
  };

  const exportMenuItemSx = {
    display: "flex", alignItems: "center",
    borderRadius: "8px", mx: 0.75, my: 0.3, px: 1.1, py: 0.8, gap: 1.25,
    "&:hover": { backgroundColor: `${tokens.accent}18` },
    "&.Mui-disabled": { opacity: 0.5 },
  };

  const exportIconBoxSx = {
    width: 28, height: 28, borderRadius: "7px",
    border: `1px solid ${tokens.accent}55`,
    backgroundColor: `${tokens.accent}14`,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: tokens.accent,
    "& svg": { fontSize: "0.95rem" },
  };

  const tableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
      borderBottom: "none",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${hoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
      color: tokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root": {
      color: "#00B8D9 !important",
      fontWeight: 600,
      textDecorationColor: "rgba(0,184,217,0.45)",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root:hover": {
      color: "#22d3ee !important",
      textDecorationColor: "#22d3ee",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: tokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor,
      color: tokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: tokens.accent,
        borderColor: tokens.accent,
      },
    },
  };

  return {
    cardSx,
    filterPanelSx,
    filterFieldLabelSx,
    primaryActionBtnSx,
    outlineBtnSx,
    exportMenuPaperSx,
    exportMenuItemBtnSx,
    exportBtnSx,
    exportMenuItemSx,
    exportIconBoxSx,
    tokens,
    tableSx,
  };
};
